"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Kwp2000Session = exports.DEFAULT_KWP2000_TIMERS = void 0;
exports.buildBmwFastTelegram = buildBmwFastTelegram;
const checksum_1 = require("./checksum");
const fastInit_1 = require("./fastInit");
const DEFAULT_TIMERS = {
    w1: 300,
    w2: 20,
    w3: 20,
    w4: 50,
    w5: 300,
    p1: 10,
    p2: 1200,
    p3: 20,
    p4: 0,
    timeoutNr78: 5000,
    retryNr78: 2
};
exports.DEFAULT_KWP2000_TIMERS = { ...DEFAULT_TIMERS };
class Kwp2000Session {
    ecuAddress;
    testerAddress;
    modeSelectPayload;
    testerPresentPayload;
    timers;
    fastInitOptions;
    state = "idle";
    lastResponseAt = 0;
    nr78Retries = new Map();
    constructor(options) {
        this.ecuAddress = options.ecuAddress;
        this.testerAddress = options.testerAddress;
        this.modeSelectPayload = Uint8Array.from(options.modeSelectPayload);
        this.testerPresentPayload = Uint8Array.from(options.testerPresentPayload);
        this.timers = { ...DEFAULT_TIMERS, ...options.timers };
        this.fastInitOptions = options.fastInit;
    }
    async startSession(transport) {
        this.assertState("idle");
        await (0, fastInit_1.sendFastInit)(transport, this.fastInitOptions);
        const keyBytes = await this.readKeyBytes(transport);
        return this.startSessionWithKeyBytes(transport, keyBytes);
    }
    async startSessionWithKeyBytes(transport, keyBytes) {
        this.assertState("idle");
        if ((0, fastInit_1.parseKeyBytes)(keyBytes) !== fastInit_1.KwpProtocols.Kwp2000) {
            throw new Error("KWP2000 key bytes not detected");
        }
        this.state = "key-bytes";
        await delay(this.timers.w4);
        const modeResponse = await this.sendRequest(transport, this.modeSelectPayload);
        this.state = "mode-select";
        await delay(this.timers.w5);
        await this.sendTesterPresent(transport);
        this.state = "active";
        return modeResponse;
    }
    async sendRequest(transport, payload) {
        if (payload.length === 0) {
            return new Uint8Array();
        }
        this.nr78Retries.clear();
        await this.waitRegenTime();
        const telegram = buildBmwFastTelegram(payload, this.ecuAddress, this.testerAddress);
        await transport.write(telegram);
        for (;;) {
            const timeout = this.nr78Retries.size > 0
                ? this.timers.timeoutNr78
                : this.timers.p2;
            const response = await readBmwFastTelegram(transport, timeout, this.timers.p1);
            const { dataLength, dataOffset } = getBmwFastDataWindow(response);
            if (dataLength === 3 &&
                response[dataOffset] === 0x7f &&
                response[dataOffset + 2] === 0x78) {
                this.addNr78(response[2]);
                if (this.nr78Retries.size === 0) {
                    this.lastResponseAt = Date.now();
                    return response;
                }
                continue;
            }
            this.removeNr78(response[2]);
            this.lastResponseAt = Date.now();
            return response;
        }
    }
    async sendTesterPresent(transport) {
        return this.sendRequest(transport, this.testerPresentPayload);
    }
    async readKeyBytes(transport) {
        const first = await transport.read(1, this.timers.w1);
        const second = await transport.read(1, this.timers.w2);
        await delay(this.timers.w3);
        return Uint8Array.from([first[0], second[0]]);
    }
    async waitRegenTime() {
        const elapsed = Date.now() - this.lastResponseAt;
        const remaining = this.timers.p3 - elapsed;
        if (remaining > 0) {
            await delay(remaining);
        }
    }
    addNr78(deviceAddress) {
        const current = this.nr78Retries.get(deviceAddress);
        if (current === undefined) {
            this.nr78Retries.set(deviceAddress, 0);
            return;
        }
        const next = current + 1;
        this.nr78Retries.delete(deviceAddress);
        if (next <= this.timers.retryNr78) {
            this.nr78Retries.set(deviceAddress, next);
        }
    }
    removeNr78(deviceAddress) {
        if (this.nr78Retries.has(deviceAddress)) {
            this.nr78Retries.delete(deviceAddress);
        }
    }
    assertState(expected) {
        if (this.state !== expected) {
            throw new Error(`Invalid session state: ${this.state}`);
        }
    }
}
exports.Kwp2000Session = Kwp2000Session;
function buildBmwFastTelegram(payload, ecuAddress, testerAddress) {
    const dataLength = payload.length;
    let headerLength = 3;
    let header = new Uint8Array(0);
    if (dataLength > 0x3f) {
        headerLength = 4;
        header = new Uint8Array(headerLength);
        header[0] = 0x80;
        header[1] = ecuAddress & 0xff;
        header[2] = testerAddress & 0xff;
        header[3] = dataLength & 0xff;
    }
    else {
        header = new Uint8Array(headerLength);
        header[0] = 0x80 | (dataLength & 0x3f);
        header[1] = ecuAddress & 0xff;
        header[2] = testerAddress & 0xff;
    }
    const telegram = new Uint8Array(headerLength + dataLength + 1);
    telegram.set(header, 0);
    telegram.set(payload, headerLength);
    telegram[telegram.length - 1] = (0, checksum_1.calcChecksumBmwFast)(telegram, 0, telegram.length - 1);
    return telegram;
}
function getBmwFastDataWindow(response) {
    const dataLengthField = response[0] & 0x3f;
    if (dataLengthField === 0) {
        if (response[3] === 0) {
            return {
                dataLength: (response[4] << 8) + response[5],
                dataOffset: 6
            };
        }
        return { dataLength: response[3], dataOffset: 4 };
    }
    return { dataLength: dataLengthField, dataOffset: 3 };
}
function calcBmwFastLength(response) {
    let length = response[0] & 0x3f;
    if (length === 0) {
        if (response[3] === 0) {
            length = (response[4] << 8) + response[5] + 6;
        }
        else {
            length = response[3] + 4;
        }
    }
    else {
        length += 3;
    }
    return length;
}
async function readBmwFastTelegram(transport, timeoutMs, telEndTimeoutMs) {
    const header = await transport.read(4, timeoutMs);
    if ((header[0] & 0xc0) !== 0x80) {
        throw new Error("Invalid BMW-FAST header byte");
    }
    let headerBytes = Array.from(header);
    if ((header[0] & 0x3f) === 0 && header[3] === 0) {
        const lengthBytes = await transport.read(2, timeoutMs);
        headerBytes = headerBytes.concat(Array.from(lengthBytes));
    }
    const totalLength = calcBmwFastLength(Uint8Array.from(headerBytes));
    const remaining = totalLength - headerBytes.length + 1;
    const tail = await transport.read(remaining, telEndTimeoutMs);
    const full = Uint8Array.from([...headerBytes, ...tail]);
    const checksum = (0, checksum_1.calcChecksumBmwFast)(full, 0, totalLength);
    if (full[totalLength] !== checksum) {
        throw new Error("BMW-FAST checksum mismatch");
    }
    return full;
}
function delay(durationMs) {
    if (durationMs <= 0) {
        return Promise.resolve();
    }
    return new Promise((resolve) => setTimeout(resolve, durationMs));
}
//# sourceMappingURL=kwp2000.js.map